import React from 'react';
import { Alert, Button, Space, Form, message, Row, Col, Input, Modal, DatePicker } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { SiZalo } from 'react-icons/si';
import { OrderModel } from '@app/interfaces/order';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import { updateStatusOrder } from '@app/store/slices/orderSlice';
import dayjs from 'dayjs';
import { DATE_TIME_FORMAT } from '@app/constants/dateFormats';

interface CompleteOrderFormProps {
  open: boolean;
  record?: OrderModel;
  onClose: () => void;
  onSuccess?: () => void;
}

const CompleteOrderForm: React.FC<CompleteOrderFormProps> = ({ open, record, onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      // Chuỗi gửi API phải giữ đúng 'HH:mm DD/MM/YYYY' — backend parse bằng strptime('%H:%M %d/%m/%Y').
      const deliveryTime = values.delivery_time
        ? dayjs(values.delivery_time).format('HH:mm DD/MM/YYYY')
        : dayjs().format('HH:mm DD/MM/YYYY');

      await dispatch(updateStatusOrder({
        orderId: Number(record?.id),
        formData: {
          status_id: 7,
          customer_name: record?.customer?.name,
          recipient_phone: values.recipient_phone,
          delivery_time: deliveryTime,
        },
      }));

      onClose();
      onSuccess?.();
    } catch (error: any) {
      if (error instanceof Error) {
        message.error(error.message);
      }
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      style={{ top: 20 }}
      closable
      footer={null}
      title={
        <span>
          <CheckCircleOutlined className="tw-mr-2 tw-text-success-teal" />
          Hoàn thành đơn hàng
        </span>
      }
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          customer_name: record?.customer?.name || '',
          recipient_phone: record?.customer?.phone || '',
          delivery_time: dayjs(),
        }}
      >
        <Alert
          type="info"
          showIcon
          icon={<SiZalo style={{ color: '#0068ff', fontSize: 16 }} />}
          style={{ marginBottom: 16 }}
          message={
            <span>
              Khi xác nhận sẽ gửi <strong>Zalo OA</strong> theo số điện thoại người nhận. Nếu số điện thoại người nhận{' '}
              <strong>khác</strong> với số của khách hàng đặt hàng, hệ thống sẽ gửi thông báo cho{' '}
              <strong>cả hai</strong>.
            </span>
          }
        />

        <Row gutter={12}>
          <Col span={24}>
            <Form.Item
              name="recipient_phone"
              label="Số điện thoại người nhận"
              rules={[{ required: true, message: 'Vui lòng nhập số điện thoại người nhận' }]}
            >
              <Input placeholder="0901234567" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="customer_name"
              label="Tên khách hàng"
              rules={[{ required: true, message: 'Vui lòng nhập tên khách hàng' }]}
            >
              <Input placeholder="Nguyễn Văn A" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              name="delivery_time"
              label="Thời gian hoàn tất giao hàng"
              rules={[{ required: true, message: 'Vui lòng chọn thời gian giao hàng' }]}
            >
              <DatePicker
                showTime={{ format: 'HH:mm' }}
                format={DATE_TIME_FORMAT}
                style={{ width: '100%' }}
                placeholder="Chọn thời gian"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row className="mt-2 text-right">
          <Col span={24}>
            <Space>
              <Button type="primary" onClick={handleSubmit}>
                <CheckCircleOutlined /> Xác nhận hoàn thành
              </Button>
              <Button onClick={onClose}>Quay lại</Button>
            </Space>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default CompleteOrderForm;
